import { useEffect, useRef } from 'react'
import { Zap, Globe, Brain, Shield, Clock, Palette } from 'lucide-react'

const features = [
    {
        icon: Zap,
        title: 'Real-time Detection',
        description: 'Instantly spot clichés as you type with our lightning-fast AI engine that analyzes your text in milliseconds.',
        gradient: 'from-yellow-400 to-orange-500',
        iconBg: 'bg-yellow-500/20',
    },
    {
        icon: Brain,
        title: 'AI-Powered Suggestions',
        description: 'Get intelligent, context-aware alternatives powered by Gemini AI that match your writing style and tone.',
        gradient: 'from-primary-400 to-ocean-500',
        iconBg: 'bg-primary-500/20',
    },
    {
        icon: Globe,
        title: 'Multi-Platform',
        description: 'Works everywhere you write - Chrome extension for Gmail, Google Docs, Twitter, LinkedIn, and more.',
        gradient: 'from-accent-400 to-pink-500',
        iconBg: 'bg-accent-500/20',
    },
    {
        icon: Shield,
        title: 'Privacy-First',
        description: 'Your text is processed securely and never stored. We prioritize your privacy and data protection.',
        gradient: 'from-green-400 to-emerald-500',
        iconBg: 'bg-green-500/20',
    },
    {
        icon: Clock,
        title: 'Save Time',
        description: 'Reduce editing time by up to 50% with automated detection and one-click replacement suggestions.',
        gradient: 'from-blue-400 to-indigo-500',
        iconBg: 'bg-blue-500/20',
    },
    {
        icon: Palette,
        title: 'Style Customization',
        description: 'Create custom rules and style guides for your brand. Perfect for content teams and agencies.',
        gradient: 'from-rose-400 to-red-500',
        iconBg: 'bg-rose-500/20',
    },
]

export default function Features() {
    const sectionRef = useRef(null)

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('visible')
                    }
                })
            },
            { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
        )

        const cards = sectionRef.current?.querySelectorAll('.feature-animate')
        cards?.forEach((card) => observer.observe(card))

        return () => observer.disconnect()
    }, [])

    return (
        <section id="features" ref={sectionRef} className="relative py-24 sm:py-32 overflow-hidden">
            {/* Background */}
            <div className="absolute inset-0 bg-slate-950" />
            <div className="absolute inset-0 bg-gradient-radial from-primary-950/50 via-transparent to-transparent" />

            {/* Content */}
            <div className="relative z-10 section-container">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <h2 className="section-title">
                        Everything You Need to{' '}
                        <span className="gradient-text">Write Better</span>
                    </h2>
                    <p className="section-subtitle">
                        Powerful features designed to help you eliminate clichés,
                        improve clarity, and make your writing stand out.
                    </p>
                </div>

                {/* Features Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                    {features.map((feature, index) => (
                        <div
                            key={feature.title}
                            className="feature-animate animate-on-scroll"
                            style={{ transitionDelay: `${index * 100}ms` }}
                        >
                            <div className="feature-card h-full group">
                                {/* Icon */}
                                <div className={`feature-icon ${feature.iconBg} group-hover:scale-110 transition-transform duration-300`}>
                                    <feature.icon className="w-7 h-7" />
                                </div>

                                {/* Content */}
                                <h3 className={`text-xl font-semibold mb-3 bg-gradient-to-r ${feature.gradient} bg-clip-text text-transparent`}>
                                    {feature.title}
                                </h3>
                                <p className="text-white/60 leading-relaxed">
                                    {feature.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Bottom CTA */}
                <div className="mt-16 text-center">
                    <p className="text-white/40 text-sm">
                        And many more features coming soon...
                    </p>
                </div>
            </div>
        </section>
    )
}
